import { BlueprintInstanceType } from '@rta/rta-blueprint-library';
import { useContext } from 'react';

import { AppContext } from '@r-app/app-context';
import { type FlatTrimPiece, TRIM_QTY_GAP, TRIM_SEAM_OVERLAP } from './helpers';
import { RenderFlatTrimGroupDimensions } from './render-flat-trim-group-dimensions';
import { renderPanelRect } from './render-panel-base';

type RenderFlatTrimGroupProps = {
	piece: FlatTrimPiece;
	groupIndex: number;
	baselineY: number;
	nameSuffixY: number;
	showTexture: boolean;
	includeLabelBox: boolean;
	fontSize: number;
	dimensionFontSize: number;
};

export const RenderFlatTrimGroup = (props: RenderFlatTrimGroupProps): React.ReactElement => {
	const { piece, groupIndex, baselineY, nameSuffixY, showTexture, includeLabelBox, fontSize, dimensionFontSize } = props;
	const { controller: { options }} = useContext(AppContext);

	const { id, count, halfWidth, leftHeight, rightHeight, x, patternOriginX, patternOriginY, namePrimary, nameSuffix, namePrimaryY } = piece;

	const fill = showTexture ? 'url(#corner-texture)' : 'darkgray';

	return (
		<g id={`corner-trim-group-${groupIndex}`}>
			<g transform={`translate(${x - patternOriginX}, ${baselineY - patternOriginY})`}>
				<g id={`corner-trim-${id}-left`}>
					{renderPanelRect({
						width: halfWidth + (TRIM_SEAM_OVERLAP / 2),
						height: leftHeight,
						center: { x: patternOriginX - (halfWidth / 2) + (TRIM_SEAM_OVERLAP / 4), y: patternOriginY - (leftHeight / 2) },
						contextCenter: { x: x - (halfWidth / 2) + (TRIM_SEAM_OVERLAP / 4), y: baselineY - (leftHeight / 2) },
						instanceType: BlueprintInstanceType.Corner,
						id,
						fill,
						stroke: 'none'
					})}
				</g>
				<g id={`corner-trim-${id}-right`}>
					{renderPanelRect({
						width: halfWidth + (TRIM_SEAM_OVERLAP / 2),
						height: rightHeight,
						center: { x: patternOriginX + (halfWidth / 2) - (TRIM_SEAM_OVERLAP / 4), y: patternOriginY - (rightHeight / 2) },
						contextCenter: { x: x + (halfWidth / 2) - (TRIM_SEAM_OVERLAP / 4), y: baselineY - (rightHeight / 2) },
						instanceType: BlueprintInstanceType.Corner,
						id,
						fill,
						stroke: 'none'
					})}
				</g>
			</g>

			{/* Qty - X */}
			<text
				x={x}
				y={namePrimaryY - TRIM_QTY_GAP}
				textAnchor="middle"
				fill="black"
				fontSize={fontSize}
				className="rta-designer-component-measure rta-noselect"
			>
				Qty - {count}
			</text>

			{/* derived type name, below the qty */}
			<text
				x={x}
				y={namePrimaryY}
				textAnchor="middle"
				fill="black"
				fontSize={fontSize * 0.8}
				className="rta-designer-component-measure rta-noselect"
			>
				{namePrimary}
			</text>
			{nameSuffix && (
				<text
					x={x}
					y={nameSuffixY}
					textAnchor="middle"
					fill="black"
					fontSize={fontSize * 0.8}
					className="rta-designer-component-measure rta-noselect"
				>
					{nameSuffix}
				</text>
			)}

			{options.showDimensions && options.showPanelOutsideDimensions && <RenderFlatTrimGroupDimensions
				{...{ piece, baselineY, includeLabelBox, dimensionFontSize, useFractions: options.useFractions }}
			/>}
		</g>
	);
};
